import React, { useEffect, useState } from "react";
import { Loader, Group, Checkbox } from "@mantine/core";
import schedulerService from "../../../services/scheduler";
import Icon from "./icons";
import { SelectDD } from "./CreateShiftDrawer";
import { useQuickBuildDock } from "./quickBuildDock";
import css from "./Scheduler.module.css";
import "./SchedulerCreate.css";

const SECTIONS = [
  { key: "shifts", label: "Staff Shifts", icon: "clock" },
  { key: "scheduling", label: "Scheduling Rules", icon: "calendar" },
  { key: "notifications", label: "Notifications", icon: "bell" },
  { key: "general", label: "General", icon: "gear" },
];

// Shift templates are stored as minutes-from-midnight; overnight shifts carry an
// endMin past 1440 (e.g. 11pm–7am = 1380 → 1860).
const pad2 = (n) => String(n).padStart(2, "0");
const minToHHMM = (m) => {
  const x = (((m % 1440) + 1440) % 1440);
  return `${pad2(Math.floor(x / 60))}:${pad2(x % 60)}`;
};
const hhmmToMin = (str) => {
  const [h, mm] = String(str).split(":").map(Number);
  return (h || 0) * 60 + (mm || 0);
};
const to12 = (m) => {
  const x = (((m % 1440) + 1440) % 1440);
  let h = Math.floor(x / 60);
  const ap = h < 12 ? "AM" : "PM";
  h = h % 12 === 0 ? 12 : h % 12;
  return `${h}:${pad2(x % 60)} ${ap}`;
};
const fmtRange = (s, e) => `${to12(s)} – ${to12(e)}`;

function Toggle({ on, onClick }) {
  return <button type="button" className={`${css.twSwitch} ${on ? css.on : ""}`} onClick={onClick} aria-pressed={on} />;
}

// SelectDD's dropdown CSS lives under the `.sd-create` scope, so wrap it here so
// the menu renders as a proper collapsed popover on the settings page.
function DD(props) {
  return <div className="sd-create"><SelectDD {...props} /></div>;
}

// Custom numeric stepper (no native number input). Minimum 1.
function Stepper({ value, onChange, min = 1 }) {
  const v = Math.max(min, Number(value) || min);
  const btn = (disabled) => ({
    width: 30, height: "100%", border: 0, background: "transparent", padding: 0,
    display: "grid", placeItems: "center", color: "var(--app-text)", fontSize: 17, lineHeight: 1,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1,
  });
  return (
    <div style={{ display: "inline-flex", alignItems: "center", height: 34, border: "1px solid var(--app-border)", borderRadius: 8, background: "var(--app-surface)", overflow: "hidden" }}>
      <button type="button" aria-label="Decrease" disabled={v <= min} onClick={() => onChange(Math.max(min, v - 1))} style={btn(v <= min)}>−</button>
      <span style={{ minWidth: 26, textAlign: "center", fontSize: 13.5, fontWeight: 600, color: "var(--app-text)", fontVariantNumeric: "tabular-nums" }}>{v}</span>
      <button type="button" aria-label="Increase" onClick={() => onChange(v + 1)} style={btn(false)}>+</button>
    </div>
  );
}

function Row({ name, sub, children }) {
  return (
    <div className={css.setToggleRow}>
      <div>
        <div className={css.strName}>{name}</div>
        {sub ? <div className={css.strSub}>{sub}</div> : null}
      </div>
      {children}
    </div>
  );
}

// Dedicated Scheduler settings page (not the setup wizard). Loads the saved
// SchedulerSettings and persists each change via PUT /schedule/settings.
export default function SchedulerSettingsPage({ facilities = [], onBack, onResetSetup }) {
  const [section, setSection] = useState("shifts");
  const [s, setS] = useState(null);
  // Quick Build floating button: docked (hidden) vs floating, persisted locally.
  const [qbDock, setQbDock] = useQuickBuildDock();

  // Shift templates (Staff Shifts) — wired to /schedule/templates CRUD.
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // null | { id?, name, start, end }
  const [savingTpl, setSavingTpl] = useState(false);

  // Schedules (weekly coverage patterns) — wired to /schedule/schedules CRUD.
  const [schedules, setSchedules] = useState(null);
  const [editingSched, setEditingSched] = useState(null); // null | { id?, name, workingDays[], shifts[], enabled }
  const [savingSched, setSavingSched] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const data = await schedulerService.getSchedulerSettings();
      if (active) setS(data || {});
      const tpls = await schedulerService.getTemplates();
      if (active) setTemplates(tpls || []);
      const scheds = await schedulerService.getSchedules();
      if (active) setSchedules(scheds || []);
    })();
    return () => {
      active = false;
    };
  }, []);

  const openNewShift = () => setEditing({ name: "", start: 7 * 60, end: 15 * 60, system: false });
  const openEditShift = (t) =>
    setEditing({ id: t.id, name: t.name, start: t.startMin, end: t.endMin, system: !!t.system });

  const saveShift = async () => {
    const name = (editing.name || "").trim();
    if (!name) return;
    let { start, end } = editing;
    if (end <= start) end += 1440; // overnight
    setSavingTpl(true);
    if (editing.id) {
      // System shifts: only the time is editable (name is locked server-side too).
      const payload = editing.system
        ? { startMin: start, endMin: end }
        : { name, startMin: start, endMin: end };
      const updated = await schedulerService.updateTemplate(editing.id, payload);
      setTemplates((list) =>
        list.map((x) => (x.id === editing.id ? updated || { ...x, ...payload } : x))
      );
    } else {
      const created = await schedulerService.createTemplate({ name, startMin: start, endMin: end });
      if (created) setTemplates((list) => [...list, created]);
    }
    setSavingTpl(false);
    setEditing(null);
  };

  // Active/inactive toggle for a shift template. Inactive templates are hidden
  // from the "Shift" picker when creating a shift/appointment.
  const toggleTemplateEnabled = async (t) => {
    const next = t.enabled === false; // currently inactive -> activate, else deactivate
    const updated = await schedulerService.updateTemplate(t.id, { enabled: next });
    setTemplates((list) => (list || []).map((x) => (x.id === t.id ? updated || { ...x, enabled: next } : x)));
  };

  const deleteShift = async (t) => {
    if (t.system) return; // system shifts cannot be deleted
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Remove the "${t.name}" shift?`)) return;
    await schedulerService.deleteTemplate(t.id);
    setTemplates((list) => list.filter((x) => x.id !== t.id));
  };

  // ── Schedules (weekly coverage patterns) ──
  const idOf = (v) => (v && typeof v === "object" ? v.id || v._id : v);
  const openNewSchedule = () => setEditingSched({ name: "", facility: facilities.length === 1 ? facilities[0].value : "", workingDays: [1, 2, 3, 4, 5], shifts: [], enabled: true });
  const openEditSchedule = (sc) =>
    setEditingSched({ id: sc.id, name: sc.name, facility: idOf(sc.facility) || "", workingDays: [...(sc.workingDays || [])], shifts: (sc.shifts || []).map((x) => ({ ...x })), enabled: sc.enabled !== false });
  const toggleSchedDay = (d) =>
    setEditingSched((e) => ({ ...e, workingDays: e.workingDays.includes(d) ? e.workingDays.filter((x) => x !== d) : [...e.workingDays, d].sort((a, b) => a - b) }));
  const toggleAllDays = () => setEditingSched((e) => ({ ...e, workingDays: e.workingDays.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6] }));
  const addSchedSlot = (tpl) =>
    setEditingSched((e) =>
      e.shifts.some((s) => idOf(s.template) === tpl.id)
        ? e
        : { ...e, shifts: [...e.shifts, { template: tpl.id, name: tpl.name, startMin: tpl.startMin, endMin: tpl.endMin, staffCount: 1 }] }
    );
  const removeSchedSlotByTemplate = (tid) => setEditingSched((e) => ({ ...e, shifts: e.shifts.filter((s) => idOf(s.template) !== tid) }));
  const setSlotCount = (i, n) => setEditingSched((e) => ({ ...e, shifts: e.shifts.map((s, idx) => (idx === i ? { ...s, staffCount: Math.max(1, n || 1) } : s)) }));
  const saveSchedule = async () => {
    const name = (editingSched.name || "").trim();
    if (!name || !editingSched.facility || !editingSched.shifts.length || !editingSched.workingDays.length) return;
    setSavingSched(true);
    const payload = { name, facility: editingSched.facility, workingDays: editingSched.workingDays, shifts: editingSched.shifts, enabled: editingSched.enabled };
    if (editingSched.id) {
      const updated = await schedulerService.updateSchedule(editingSched.id, payload);
      setSchedules((list) => (list || []).map((x) => (x.id === editingSched.id ? updated || { ...x, ...payload } : x)));
    } else {
      const created = await schedulerService.createSchedule(payload);
      if (created) setSchedules((list) => [...(list || []), created]);
    }
    setSavingSched(false);
    setEditingSched(null);
  };
  const removeSchedule = async (sc) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete the "${sc.name}" schedule?`)) return;
    if (await schedulerService.deleteSchedule(sc.id)) setSchedules((list) => (list || []).filter((x) => x.id !== sc.id));
  };
  const toggleScheduleEnabled = async (sc) => {
    const next = sc.enabled === false;
    const updated = await schedulerService.updateSchedule(sc.id, { name: sc.name, facility: idOf(sc.facility) || null, workingDays: sc.workingDays, shifts: sc.shifts, enabled: next });
    setSchedules((list) => (list || []).map((x) => (x.id === sc.id ? updated || { ...x, enabled: next } : x)));
  };
  const DOW = [["S", 0], ["M", 1], ["T", 2], ["W", 3], ["T", 4], ["F", 5], ["S", 6]];
  const DOW_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  // Compact working-days summary for the list rows.
  const fmtDays = (days) => {
    const d = [...new Set(days || [])].sort((a, b) => a - b);
    if (!d.length) return "No days";
    if (d.length === 7) return "Every day";
    if (d.join() === "1,2,3,4,5") return "Mon–Fri";
    if (d.join() === "0,6") return "Weekends";
    return d.map((x) => DOW_FULL[x]).join(", ");
  };
  const facilityName = (fid) => facilities.find((f) => f.value === idOf(fid))?.label || "Facility";
  // Every active shift is selectable for a schedule (the schedule's facility is a
  // separate field). Only inactive shifts are excluded.
  const activeTemplates = () => (templates || []).filter((t) => t.enabled !== false);

  const save = (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    schedulerService.updateSchedulerSettings(next);
  };
  const rules = s?.rules || {};
  const setRule = (k, v) => save({ rules: { ...rules, [k]: v } });
  // Mirrors the backend default: 24h + 1h reminders out of the box.
  const reminders = s?.reminders || { enabled: true, leadTimes: ["24h", "1h"] };
  const hasLead = (t) => (reminders.leadTimes || []).includes(t);
  const toggleLead = (t) => {
    const set = new Set(reminders.leadTimes || []);
    if (set.has(t)) set.delete(t);
    else set.add(t);
    save({ reminders: { ...reminders, leadTimes: [...set] } });
  };

  return (
    <>
      <button type="button" className={css.backNav} onClick={onBack}>
        <span className={css.bnIc}><Icon name="chevronLeft" size={15} /></span>
        Back to Calendar
      </button>

      {!s ? (
        <Group position="center" p={60}><Loader color="var(--app-primary)" /></Group>
      ) : (
        <div className={css.settingsLayout}>
          <nav className={css.settingsNav}>
            {SECTIONS.map((sec) => (
              <button key={sec.key} type="button" className={section === sec.key ? css.active : ""} onClick={() => setSection(sec.key)}>
                <Icon name={sec.icon} size={17} /> {sec.label}
              </button>
            ))}
          </nav>

          <div>
            {section === "shifts" && (
              <div className={css.settingsCard}>
                <div className={css.scHead}>
                  <div>
                    <h3>Staff Shifts</h3>
                    <div className={css.scSub}>
                      Predefined shift times staff can be assigned to. These appear as templates when creating a shift.
                    </div>
                  </div>
                  {!editing ? (
                    <button type="button" className={css.btnXs} onClick={openNewShift}>
                      <Icon name="plus" size={15} /> Add Shift
                    </button>
                  ) : null}
                </div>
                <div className={css.scBody}>
                  {editing ? (
                    <div className={css.tplEditor}>
                      <div className={css.tplEditorHead}>
                        {!editing.id ? "New shift" : editing.system ? "Edit shift time" : "Edit shift"}
                      </div>
                      <div className={css.tplRow}>
                        <div className={`${css.tplField} ${css.tplName}`}>
                          <label>Shift name {editing.system ? null : <span className={css.req}>*</span>}</label>
                          <input
                            className={css.inp}
                            value={editing.name}
                            autoFocus={!editing.system}
                            disabled={editing.system}
                            placeholder="e.g. Morning, Overnight"
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          />
                          {editing.system ? (
                            <span className={css.tplHint}>System shift. Only the time can be changed.</span>
                          ) : null}
                        </div>
                        <div className={css.tplField}>
                          <label>Start <span className={css.req}>*</span></label>
                          <div className={css.tplTime}>
                            <input
                              className={css.inp}
                              type="time"
                              value={minToHHMM(editing.start)}
                              onChange={(e) => setEditing({ ...editing, start: hhmmToMin(e.target.value) })}
                            />
                            <span
                              className={css.tplTimeIc}
                              onClick={(e) => e.currentTarget.parentElement.querySelector("input")?.showPicker?.()}
                            >
                              <Icon name="clock" size={15} />
                            </span>
                          </div>
                        </div>
                        <div className={css.tplField}>
                          <label>End <span className={css.req}>*</span></label>
                          <div className={css.tplTime}>
                            <input
                              className={css.inp}
                              type="time"
                              value={minToHHMM(editing.end)}
                              onChange={(e) => setEditing({ ...editing, end: hhmmToMin(e.target.value) })}
                            />
                            <span
                              className={css.tplTimeIc}
                              onClick={(e) => e.currentTarget.parentElement.querySelector("input")?.showPicker?.()}
                            >
                              <Icon name="clock" size={15} />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={css.tplFoot}>
                        <button type="button" className={`${css.btnXsGhost} ${css.tplCancel}`} onClick={() => setEditing(null)} disabled={savingTpl}>
                          Cancel
                        </button>
                        <button type="button" className={css.btnXs} onClick={saveShift} disabled={savingTpl || !editing.name.trim()}>
                          {savingTpl ? "Saving…" : editing.id ? "Save Changes" : "Add Shift"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {templates === null ? (
                    <Group position="center" p={24}><Loader size="sm" color="var(--app-primary)" /></Group>
                  ) : templates.length === 0 && !editing ? (
                    <div className={css.shiftEmpty}>No shifts yet. Add one to use as a template when creating shifts.</div>
                  ) : (
                    [...templates]
                      .sort(
                        (a, b) =>
                          Number(!!b.system) - Number(!!a.system) || a.startMin - b.startMin
                      )
                      .map((t) => (
                      <div key={t.id} className={css.shiftRow}>
                        <div className={css.srMain} style={t.enabled === false ? { opacity: 0.55 } : undefined}>
                          <span className={css.srNameText}>{t.name}</span>
                          <span className={`${css.srTag} ${t.system ? "" : css.srTagCustom}`}>
                            {t.system ? "System" : "Custom"}
                          </span>
                          <span className={css.srTime}>{fmtRange(t.startMin, t.endMin)}</span>
                          {t.enabled === false ? <span className={`${css.srTag}`} style={{ color: "var(--app-text-muted)" }}>Inactive</span> : null}
                        </div>
                        <div className={css.srActions}>
                          <Toggle on={t.enabled !== false} onClick={() => toggleTemplateEnabled(t)} />
                          <button
                            type="button"
                            className={css.srIconBtn}
                            aria-label={t.system ? "Edit shift time" : "Edit shift"}
                            title={t.system ? "Edit time" : "Edit"}
                            onClick={() => openEditShift(t)}
                          >
                            <Icon name="edit" size={15} />
                          </button>
                          {!t.system ? (
                            <button
                              type="button"
                              className={`${css.srIconBtn} ${css.srIconDanger}`}
                              aria-label="Delete shift"
                              title="Delete"
                              onClick={() => deleteShift(t)}
                            >
                              <Icon name="trash" size={15} />
                            </button>
                          ) : (
                            <span className={css.srIconSpacer} aria-hidden="true" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {section === "scheduling" && (
              <>
              <div className={css.settingsCard}>
                <div className={css.scHead}>
                  <div>
                    <h3>Scheduling Rules</h3>
                    <div className={css.scSub}>Guardrails applied when building the schedule.</div>
                  </div>
                </div>
                <div className={css.scBody}>
                  <Row name="Warn on double-booking" sub="Flag when a staff member has overlapping shifts.">
                    <Toggle on={rules.warnDoubleBooking !== false} onClick={() => setRule("warnDoubleBooking", !(rules.warnDoubleBooking !== false))} />
                  </Row>
                  <Row name="Overtime alerts" sub="Warn when a weekly schedule exceeds the overtime threshold.">
                    <Toggle on={rules.overtimeAlerts !== false} onClick={() => setRule("overtimeAlerts", !(rules.overtimeAlerts !== false))} />
                  </Row>
                  <Row name="Overtime threshold" sub="Weekly hours before overtime applies.">
                    <div style={{ width: 180 }}>
                      <DD
                        value={String(rules.overtimeThreshold ?? 40)}
                        options={[40, 37.5, 44].map((h) => ({ value: String(h), label: `${h} hours` }))}
                        onChange={(v) => setRule("overtimeThreshold", Number(v))}
                      />
                    </div>
                  </Row>
                  <Row name="Maximum shift length" sub="Longest a single shift can be scheduled.">
                    <div style={{ width: 180 }}>
                      <DD
                        value={String(rules.maxShiftLength ?? 12)}
                        options={[{ v: 8, l: "8 hours" }, { v: 10, l: "10 hours" }, { v: 12, l: "12 hours" }, { v: 0, l: "No limit" }].map((o) => ({ value: String(o.v), label: o.l }))}
                        onChange={(v) => setRule("maxShiftLength", Number(v))}
                      />
                    </div>
                  </Row>
                  <Row name="Restrict clock-in to scheduled shifts" sub="Prevent staff from clocking in or out when they have no shift scheduled.">
                    <Toggle on={rules.restrictClockInToScheduled !== false} onClick={() => setRule("restrictClockInToScheduled", !(rules.restrictClockInToScheduled !== false))} />
                  </Row>
                  <Row name="Allow staff to add time entries" sub="Let staff manually log time for missed clock-ins or clock-outs. Entries are flagged for review.">
                    <Toggle on={rules.allowManualEntry !== false} onClick={() => setRule("allowManualEntry", !(rules.allowManualEntry !== false))} />
                  </Row>
                  <Row name="Automatically show open shifts in calendar" sub="Surface every schedule slot that isn't covered by an assigned shift as an open shift to fill.">
                    <Toggle on={!!rules.autoShowOpenShifts} onClick={() => setRule("autoShowOpenShifts", !rules.autoShowOpenShifts)} />
                  </Row>
                  <Row name="Minimum notice for time off" sub="Ask staff to request time off this far in advance. Shown as a warning when they request sooner; requests can still be submitted.">
                    <div style={{ width: 180 }}>
                      <DD
                        value={String(rules.timeOffMinNoticeDays ?? 14)}
                        options={[{ value: "0", label: "None" }, { value: "7", label: "1 week" }, { value: "14", label: "2 weeks" }, { value: "30", label: "1 month" }, { value: "60", label: "2 months" }]}
                        onChange={(v) => setRule("timeOffMinNoticeDays", Number(v))}
                      />
                    </div>
                  </Row>
                </div>
              </div>

              {/* Schedules — weekly coverage patterns that drive open shifts */}
              <div className={css.settingsCard} style={{ marginTop: 16 }}>
                <div className={css.scHead}>
                  <div>
                    <h3>Schedules</h3>
                    <div className={css.scSub}>Set which shifts run on which days per facility, and how many staff each needs.</div>
                  </div>
                  {!editingSched ? (
                    <button type="button" className={css.btnXs} onClick={openNewSchedule}>
                      <Icon name="plus" size={15} /> Add Schedule
                    </button>
                  ) : null}
                </div>
                <div className={css.scBody}>
                  {editingSched ? (
                    <div className={css.tplEditor}>
                      <div className={css.tplEditorHead}>{editingSched.id ? "Edit schedule" : "New schedule"}</div>
                      <div className={css.tplRow}>
                        <div className={css.tplField} style={{ marginBottom: 14, flex: "1 1 0%", minWidth: 0 }}>
                          <label>Schedule name <span className={css.req}>*</span></label>
                          <input className={css.inp} value={editingSched.name} autoFocus placeholder="e.g. Weekday Coverage" onChange={(e) => setEditingSched({ ...editingSched, name: e.target.value })} />
                        </div>
                        <div className={css.tplField} style={{ marginBottom: 14, flex: "1 1 0%", minWidth: 0 }}>
                          <label>Facility <span className={css.req}>*</span></label>
                          <DD
                            placeholder="Select a facility"
                            value={editingSched.facility || ""}
                            options={facilities.map((f) => ({ value: f.value, label: f.label }))}
                            onChange={(v) => setEditingSched({ ...editingSched, facility: v || "", shifts: [] })}
                          />
                        </div>
                      </div>

                      <div className={css.tplField} style={{ marginBottom: 14 }}>
                        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>Working days <span className={css.req}>*</span></span>
                          <Checkbox
                            size="xs"
                            label="All days"
                            color="yellow"
                            checked={editingSched.workingDays.length === 7}
                            indeterminate={editingSched.workingDays.length > 0 && editingSched.workingDays.length < 7}
                            onChange={toggleAllDays}
                            styles={{ label: { fontSize: 12.5, fontWeight: 600, color: "var(--app-text-muted)", paddingLeft: 6 } }}
                          />
                        </label>
                        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                          {DOW.map(([lbl, d]) => {
                            const on = editingSched.workingDays.includes(d);
                            return (
                              <button key={d} type="button" title={DOW_FULL[d]} onClick={() => toggleSchedDay(d)}
                                style={{ width: 38, height: 38, borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                                  border: `1px solid ${on ? "var(--app-primary)" : "var(--app-border)"}`,
                                  background: on ? "var(--app-primary)" : "var(--app-surface)",
                                  color: on ? "var(--app-on-primary)" : "var(--app-text-muted)" }}>
                                {lbl}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className={css.tplField}>
                        <label>Shifts in a working day <span className={css.req}>*</span></label>
                        {(() => {
                          const list = activeTemplates();
                          if (!list.length) return <div className={css.tplHint} style={{ margin: "6px 0 0" }}>No shifts yet. Create them under Staff Shifts.</div>;
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                              {list.map((t) => {
                                const slotIdx = editingSched.shifts.findIndex((s) => idOf(s.template) === t.id);
                                const included = slotIdx >= 0;
                                return (
                                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${included ? "var(--app-primary)" : "var(--app-border)"}`, background: included ? "var(--app-tint-amber)" : "var(--app-surface)" }}>
                                    <Checkbox
                                      size="sm"
                                      color="yellow"
                                      checked={included}
                                      onChange={() => (included ? removeSchedSlotByTemplate(t.id) : addSchedSlot(t))}
                                    />
                                    <div style={{ flex: 1, cursor: "pointer" }} onClick={() => (included ? removeSchedSlotByTemplate(t.id) : addSchedSlot(t))}>
                                      <div style={{ fontWeight: 600, color: "var(--app-text)" }}>{t.name}</div>
                                      <div style={{ fontSize: 12, color: "var(--app-text-muted)" }}>{fmtRange(t.startMin, t.endMin)}</div>
                                    </div>
                                    {included ? (
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ fontSize: 12.5, color: "var(--app-text-muted)" }}>Staff needed</span>
                                        <Stepper value={editingSched.shifts[slotIdx].staffCount} onChange={(n) => setSlotCount(slotIdx, n)} />
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      <div className={css.tplFoot}>
                        <button type="button" className={`${css.btnXsGhost} ${css.tplCancel}`} onClick={() => setEditingSched(null)} disabled={savingSched}>Cancel</button>
                        <button type="button" className={css.btnXs} onClick={saveSchedule} disabled={savingSched || !editingSched.name.trim() || !editingSched.facility || !editingSched.shifts.length || !editingSched.workingDays.length}>
                          {savingSched ? "Saving…" : editingSched.id ? "Save Changes" : "Add Schedule"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {schedules === null ? (
                    <Group position="center" p={24}><Loader size="sm" color="var(--app-primary)" /></Group>
                  ) : schedules.length === 0 && !editingSched ? (
                    <div className={css.shiftEmpty}>No schedules yet. Add one to define your weekly coverage and auto-fill open shifts.</div>
                  ) : (
                    schedules.map((sc) => (
                      <div key={sc.id} className={css.shiftRow}>
                        <div className={css.srMain} style={sc.enabled === false ? { opacity: 0.55 } : undefined}>
                          <span className={css.srNameText} style={{ fontWeight: 600 }}>{sc.name}</span>
                          <span className={`${css.srTag} ${css.srTagCustom}`} style={{ width: "auto", padding: "2px 10px", whiteSpace: "nowrap" }}>{sc.facility?.name || facilityName(sc.facility)}</span>
                          {sc.enabled === false ? <span className={css.srTag} style={{ width: "auto", padding: "2px 10px", whiteSpace: "nowrap", color: "var(--app-text-muted)" }}>Inactive</span> : null}
                          <span style={{ fontSize: 12, color: "var(--app-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {fmtDays(sc.workingDays)} · {(sc.shifts || []).length} shift{(sc.shifts || []).length === 1 ? "" : "s"} · {(sc.shifts || []).reduce((a, x) => a + (x.staffCount || 1), 0)} staff/day
                          </span>
                        </div>
                        <div className={css.srActions}>
                          <Toggle on={sc.enabled !== false} onClick={() => toggleScheduleEnabled(sc)} />
                          <button type="button" className={css.srIconBtn} aria-label="Edit schedule" title="Edit" onClick={() => openEditSchedule(sc)}><Icon name="edit" size={15} /></button>
                          <button type="button" className={`${css.srIconBtn} ${css.srIconDanger}`} aria-label="Delete schedule" title="Delete" onClick={() => removeSchedule(sc)}><Icon name="trash" size={15} /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              </>
            )}

            {section === "notifications" && (
              <div className={css.settingsCard}>
                <div className={css.scHead}>
                  <div>
                    <h3>Notifications</h3>
                    <div className={css.scSub}>Shift reminders sent to assigned staff before their shifts.</div>
                  </div>
                </div>
                <div className={css.scBody}>
                  <Row name="Enable shift reminders" sub="Notify assigned staff before their shifts when a schedule is published.">
                    <Toggle on={reminders.enabled !== false} onClick={() => save({ reminders: { ...reminders, enabled: !(reminders.enabled !== false) } })} />
                  </Row>
                  <Row name="24 hours before" sub="A day-ahead heads up.">
                    <Toggle on={hasLead("24h")} onClick={() => toggleLead("24h")} />
                  </Row>
                  <Row name="1 hour before" sub="A final nudge before clock-in.">
                    <Toggle on={hasLead("1h")} onClick={() => toggleLead("1h")} />
                  </Row>
                </div>
              </div>
            )}

            {section === "general" && (
              <div className={css.settingsCard}>
                <div className={css.scHead}>
                  <div>
                    <h3>General</h3>
                    <div className={css.scSub}>Display and setup preferences for the scheduler.</div>
                  </div>
                </div>
                <div className={css.scBody}>
                  <Row name="Week starts on" sub="First day of the week shown on the calendar.">
                    <div style={{ width: 180 }}>
                      <DD
                        value={s.weekStartsOn || "Sunday"}
                        options={[{ value: "Sunday", label: "Sunday" }, { value: "Monday", label: "Monday" }]}
                        onChange={(v) => save({ weekStartsOn: v })}
                      />
                    </div>
                  </Row>
                  <Row name="Quick Build button" sub="Show a floating, draggable button on the calendar for rapidly adding shifts and appointments.">
                    <Toggle on={qbDock.popped} onClick={() => setQbDock({ popped: !qbDock.popped })} />
                  </Row>
                  <Row name="Re-run setup wizard" sub="Walk through the Scheduler setup again to reconfigure operating hours and rules.">
                    <button type="button" className={css.btnG} onClick={onResetSetup}>
                      <Icon name="rocket" size={15} /> Reset setup
                    </button>
                  </Row>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
